import { useContext } from 'react'
import { useExperiences as useExperiencesChansey } from 'react-chansey/lib/provider'

import { path, unpick, mapObj } from '../../utils/obj'
import { contentTypeId } from '../../utils/contentful'

import { FsCore } from '../../types/fs-core'

import {
  EVCComponentContext,
  EVCDataContext,
  EVCOptionsContext,
  EVTrackingContext,
  SlimlineContext,
} from './provider'

import { forceHydration } from '../../server/helpers/env'

const isNil = (value) => value === undefined || value === null
const notNil = (value) => !isNil(value)

const whenNotNil = (value, defaultValue) => (notNil(value) ? value : defaultValue)

export const useExperiences = useExperiencesChansey

export const useTracking = () => useContext(EVTrackingContext).track

export const useDataSelector = (s = (i) => i, defaultValue) => {
  const data = useContext(EVCDataContext)
  if (typeof s === 'function') whenNotNil(s(data), defaultValue)
  if (Array.isArray(s)) return path(s, data, defaultValue)
  return data
}

const useDoHydrate = () => useDataSelector(['toHydrate'], [])

export const useForceHydration = () => useDataSelector(['forceHydration'], forceHydration)

export const useDoHydrateComponent = (id) => {
  const doHydrateIds = useDoHydrate()
  const forceHydration = useForceHydration()
  const doHydrate = forceHydration || doHydrateIds.includes(id)
  return doHydrate
}

export const usePalette = (palette) => {
  const Slimline = useContext(SlimlineContext)

  const includes = useIncludes()
  const styles = unpick(['name', 'sys'], useEntity(palette))

  return mapObj((color) => {
    const id = path(['sys', 'id'], color)
    Slimline.push(id)

    const hexcode = path([id, 'fields', 'hexcode'], includes)
    return `#${hexcode}`
  }, styles)
}

export const useOptions = () => {
  const Options = useContext(EVCOptionsContext)
  return Options
}

export const useBrand = (): FsCore.FSBrand => useMeta(['brand'], 'money')?.toLowerCase()

export const getEntity = (props, includes = {}) => {
  if (Object.keys(includes).length === 0) {
    // eslint-disable-next-line no-console
    console.log('empty includes')
  }
  const id = path(['id'], props) || path(['sys', 'id'], props)
  const entity = path([id], includes)

  if (id && !entity) {
    // eslint-disable-next-line no-console
    console.log(`No entity in getEntity hook for id: ${id}`)
    return { err: `ENOENTITY: unable to find entity for id ${id} in getEntity` }
  }

  return entity ? { ...props, ...entity.fields, sys: entity.sys } : {}
}

export const useDontLazyload = () => useDataSelector(['dontLazyLoad'], false)

export const useIncludes = () => useDataSelector(['includes'])

export const useEntity = (props) => {
  return getEntity(props, useIncludes())
}

export const useEntities = (entries) => {
  return entries.map(useEntity)
}

const firstValidFlag = (map, experiences = {}, flags = {}) => {
  return Object.entries(flags).find(([flag, variant]) => {
    if (!Object.prototype.hasOwnProperty.call(experiences, flag)) {
      return false
    }

    if (typeof experiences[flag].value !== 'boolean') {
      // eslint-disable-next-line no-console
      console.error(
        `EINVALIDCONFIG: invalid flag ${flag} used in meta when rendering - not boolean`,
      )
      return false
    }

    return experiences[flag].value === true && map[variant]
  })
}

export const useComponent = (props = {}) => {
  const flagState = useMeta('releaseFlags')
  const experiences = useExperiences()

  // TODO: fix that below
  let cMap = useContext(EVCComponentContext)
  if (cMap.EVCoreDefaultComponents) {
    cMap = cMap.EVCoreDefaultComponents
  }

  const componentId = props.tag || contentTypeId(props)
  const component = cMap[componentId]
  if (!component) {
    return [`ENOCOMPONENT: unable to find component ID in ${props?.sys?.contentTypeId}`]
  }
  if (component && component.variations) {
    const componentMap = component.variations

    if (!componentMap.default) {
      return [`EINVALIDCONFIG: invalid config for ${componentId} - missing default map`]
    }

    const [, variant] = firstValidFlag(componentMap, experiences, flagState) || []
    const { ...config } = component

    const variantComponent = variant ? componentMap[variant] : componentMap.default

    return [null, variantComponent, component.wrapperConfig, component.features ?? {}, config]
  }

  return [
    `ENOCOMPONENT: unable to find component for ${[props.tag, contentTypeId(props)]
      .filter((i) => i)
      .join('||')}`,
  ]
}

export const useMeta = (path, defaultValue) => {
  return useDataSelector(['meta'].concat(path), defaultValue)
}

export const useComponentMeta = (component, variant, defaultValue = {}) => {
  const path = variant ? [component, variant] : [component]
  return useMeta(path, defaultValue)
}
